import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, TimeoutError } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { HTTP_MESSAGES } from '../../i18n/http.messages';
import { ApiError } from '../api-error';
import { ApiNotificationsService } from '../api-notifications.service';

/**
 * Pon `SILENT_ERRORS` a true en una petición cuando el componente va a mostrar el error inline
 * (un `ErrorState`, un formulario) y no quieres además la notificación global.
 */
export const SILENT_ERRORS = new HttpContextToken<boolean>(() => false);

/** `TimeoutError` de RxJS (el `timeout(300000)` de `HttpInterceptService` de Sirius) como `ApiError` de red. */
export function timeoutApiError(url: string | null): ApiError {
  return new ApiError({ status: 0, message: HTTP_MESSAGES.errors.timeout, url });
}

/**
 * Traduce los errores HTTP a `ApiError` y aplica la política común (ux-spec §8.9): 401 → cierra la sesión de Sirius;
 * 403 → avisa del access que falta (el JWT es la verdad); el resto → notificación con
 * código, mensaje de la API, acción sugerida y `x-request-id` copiable. Los 404/409 se dejan al componente
 * (son información de pantalla), salvo que la petición no lo silencie. El `TimeoutError` que puede emitir el
 * `HttpInterceptService` de Sirius (no es un `HttpErrorResponse`) se trata como un fallo de red.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const notifications = inject(ApiNotificationsService);
  return next(req).pipe(
    catchError((raw: unknown) => {
      const silent = req.context.get(SILENT_ERRORS);
      if (raw instanceof TimeoutError) {
        const error = timeoutApiError(req.urlWithParams);
        if (!silent) notifications.error(error);
        return throwError(() => error);
      }
      if (!(raw instanceof HttpErrorResponse)) return throwError(() => raw);
      const error = ApiError.from(raw);
      if (error.isUnauthorized) {
        auth.handleUnauthorized();
      } else if (error.isForbidden) {
        if (!silent) notifications.forbidden(error);
      } else if (!silent && !error.isNotFound && !error.isConflict) {
        notifications.error(error);
      }
      return throwError(() => error);
    }),
  );
};
